import { adminConfig } from '../config/env';
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { RequireAdmin } from '../auth/RequireAdmin';
import { AppShell } from '../shell/AppShell';
import { RouteLoadingFallback } from './RouteLoadingFallback';
import { Stack } from '@mantine/core';
import { OdooAccess } from '../billing/OdooAccess';

const AccountPage = lazy(() => import('../account/AccountPage').then(m => ({ default: m.AccountPage })));
const RegistrationPage = lazy(() => import('../account/RegistrationPage').then(m => ({ default: m.RegistrationPage })));
const PricingPage = lazy(() => import('../pricing/PricingPage').then(m => ({ default: m.PricingPage })));
const DashboardPage = lazy(() => import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const LoginPage = lazy(() => import('../pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const PlaceholderPage = lazy(() => import('../pages/PlaceholderPage').then((m) => ({ default: m.PlaceholderPage })));
const ReservesPage = lazy(() => import('../reserves/ReservesPage').then((m) => ({ default: m.ReservesPage })));
const ServicesPage = lazy(() => import('../reserves/ReservesPage').then((m) => ({ default: m.ServicesPage })));
const ServiceDetailPage = lazy(() => import('../service-detail/ServiceDetailPage').then((m) => ({ default: m.ServiceDetailPage })));
const ServiceEditorPage = lazy(() => import('../service-detail/ServiceEditorPage').then((m) => ({ default: m.ServiceEditorPage })));
const NotificationsPage = lazy(() => import('../notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const VtcPage = lazy(() => import('../vtc/VtcPage').then((m) => ({ default: m.VtcPage })));
const AdminWorkspaceScope = lazy(() => import('../workspace/AdminWorkspaceScope').then((m) => ({ default: m.AdminWorkspaceScope })));
const NewServicePage = lazy(() => import('../service-creation/NewServicePage').then((m) => ({ default: m.NewServicePage })));
const BillingPage = lazy(() => import('../billing/BillingPage').then((m) => ({ default: m.BillingPage })));
const AdminRequestsPage = lazy(() => import('../requests/AdminRequestsPage').then((m) => ({ default: m.AdminRequestsPage })));
const OperatorsPage = lazy(() => import('../operators/OperatorsPage').then((m) => ({ default: m.OperatorsPage })));
const FleetManagementPage = lazy(() => import('../fleet/management/FleetManagementPage').then((m) => ({ default: m.FleetManagementPage })));
const CompliancePage = lazy(() => import('../compliance/CompliancePage').then((m) => ({ default: m.CompliancePage })));
const AccountingPage = lazy(() => import('../accounting/AccountingPage').then((m) => ({ default: m.AccountingPage })));
const InvoicesPage = lazy(() => import('../verifactu/InvoicesPage').then((m) => ({ default: m.InvoicesPage })));

function lazyRoute(element: ReactNode): ReactNode {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      {element}
    </Suspense>
  );
}

/**
 * Mapa de rutas del panel. `/login` es público; todo lo demás vive bajo el guard
 * fail-closed `RequireAdmin` + el caparazón `AppShell`. Paridad de IA con el
 * GoRouter de admin_app (dashboard/reserves/new-service/invoices/expenses/vtc/
 * admin-requests).
 */
export const appRoutes: RouteObject[] = [
  { path: '/account', element: lazyRoute(<AccountPage />) },
  { path: '/profile', element: lazyRoute(<AccountPage />) },
  { path: '/pending-approval', element: lazyRoute(<AccountPage />) },
  { path: '/register', element: lazyRoute(<RegistrationPage />) },
  { path: '/login', element: lazyRoute(<LoginPage />) },
  {
    path: '/',
    element: <RequireAdmin />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { element: lazyRoute(<AdminWorkspaceScope />), children: [
            { path: 'dashboard', element: lazyRoute(<DashboardPage />) },
            { path: 'reserves', element: lazyRoute(<ReservesPage />) },
            { path: 'services', element: lazyRoute(<ServicesPage />) },
            { path: 'reserves/:id', element: lazyRoute(<ServiceDetailPage reserves />) },
            { path: 'services/:id', element: lazyRoute(<ServiceDetailPage />) },
            { path: 'reserves/:id/edit', element: lazyRoute(<ServiceEditorPage reserves duplicate={false} />) },
            { path: 'reserves/:id/duplicate', element: lazyRoute(<ServiceEditorPage reserves duplicate />) },
            { path: 'services/:id/duplicate', element: lazyRoute(<ServiceEditorPage reserves={false} duplicate />) },
            { path: 'notifications', element: lazyRoute(<NotificationsPage />) },
            { path: 'new-service', element: lazyRoute(<NewServicePage />) },
            { path: 'invoices', element: <Stack gap="lg"><OdooAccess />{lazyRoute(adminConfig.verifactuReadOnlyEnabled ? <InvoicesPage /> : <BillingPage />)}</Stack> },
            { path: 'accounting', element: lazyRoute(<AccountingPage />) },
            { path: 'compliance', element: lazyRoute(<CompliancePage />) },
            { path: 'operators', element: lazyRoute(<OperatorsPage />) },
            { path: 'operators/new', element: lazyRoute(<OperatorsPage create />) },
            { path: 'operators/:id', element: lazyRoute(<OperatorsPage />) },
            { path: 'pricing', element: lazyRoute(<PricingPage />) },
            { path: 'fleet', element: lazyRoute(<FleetManagementPage />) },
            { path: 'vtc', element: lazyRoute(<VtcPage />) },
            { path: 'admin-requests', element: lazyRoute(<AdminRequestsPage />) },
          ] },
          { path: 'expenses', element: lazyRoute(<PlaceholderPage title="Gastos" />) },
        ],
      },
    ],
  },
  { path: '*', element: lazyRoute(<NotFoundPage />) },
];
